import { Plugin } from "@/types/enums/Plugin";
import { RegistrationMode } from "@/types/enums/RegistrationMode";
import { RegistrationType } from "@/types/enums/RegistrationType";
import {
  DigestInterval,
  KpiMetric,
  ThresholdDirection,
  Weekday,
} from "@/types/EventGoal";
import { ConditionGroup } from "@/types/EventRule";
import { EventRoleCreationDTO } from "@/types/EventRoleCreationDTO";
import { Filter } from "@/types/Filter";

// ── Section enum ──
export enum TemplateSectionType {
  GENERAL = "general",
  PLUGINS = "plugins",
  FIELDS = "fields",
  RULES = "rules",
  PIPES = "pipes",
  DEVICES = "devices",
  SLOTTING = "slotting",
  WEBSITE = "website",
  EMAIL = "email",
  SEATING = "seating",
  DASHBOARD = "dashboard",
  SENDER = "sender",
  ATTACHMENTS = "attachments",
  DOCUMENTS = "documents",
  COLLABORATORS = "collaborators",
  GOALS = "goals",
  TABLE_VIEWS = "tableViews",
  GUEST_IMPORT = "guestImport",
}

// ── Main template entity ──
export interface EventTemplate {
  templateId: string;
  companyId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  tags?: string[];
  includedSections: TemplateSectionType[];

  // Embedded section payloads (only GENERAL, PLUGINS, RULES stay embedded)
  eventConfig?: TemplateEventConfig;
  plugins?: Plugin[];
  rules?: TemplateRules;
  goalConfig?: TemplateGoalConfig;
  /** "tableViews" section — captured event-scoped table views + filters. */
  tableViewConfig?: TemplateTableConfig;
  /** "guestImport" section — CRM contact filter, auto-imported on apply. */
  guestImportConfig?: TemplateGuestImportConfig | null;
  sidebarOrder?: string[];

  dashboardLayout?: TemplateDashboardCard[];
  deviceConfigs?: TemplateDeviceConfig[];
  emailTemplates?: TemplateEmailConfig[];
  workflows?: TemplateWorkflow[];
  offeringConfigs?: TemplateOfferingConfig[];
  attachmentConfigs?: TemplateAttachmentConfig[];
  defaultCollaborators?: EventRoleCreationDTO[];

  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// ── Embedded section types (remain in EventTemplate doc) ──

export interface TemplateEventConfig {
  namePattern?: string;
  timeZone?: string;
  registrationType?: RegistrationType;
  durationMinutes?: number;
  /** Carries the source event's expectedCapacity. */
  capacity?: number;
  defaultRegistrationMode?: RegistrationMode;
  description?: string;
  onlineUrl?: string;
  guestActionTokenTtlDays?: number;
  portalTokenTtlDays?: number;
  bulkActionLimit?: number;
  guestDetailLayout?: Record<string, unknown>;
  /** Named guest-detail layouts, cloned verbatim (entry ids included). */
  guestDetailLayouts?: Record<string, unknown>[];
  /** Event-meta additional fields as raw {type, value, name} maps. */
  additionalFields?: Record<string, unknown>;
  fieldDisplayColors?: Record<string, Record<string, string>>;
  fieldConditions?: Record<string, Record<string, unknown>>;
  fieldRequired?: Record<string, boolean>;
  location?: {
    locationName?: string;
    address?: string;
    address2?: string;
    zipCode?: string;
    city?: string;
    country?: string;
  };
}

export interface TemplateRules {
  globalGuestLimitEnabled?: boolean;
  globalGuestLimit?: number;
  globalCountingStatuses?: string[];
  globalCountingConditions?: ConditionGroup;
  globalWaitlistEnabled?: boolean;
  globalWaitlistLimit?: number;
  defaultRegistrationConditions?: ConditionGroup;
  defaultRegistrationMessage?: string;
}

/** Templated KPI row — EventGoalKpi minus deadline (deliberately not
 *  templated) and the server-managed alert-state fields. The server strips
 *  unknown fields and mints ids for id-less rows on apply. */
export interface TemplateGoalKpi {
  id?: string;
  metric: KpiMetric;
  target: number;
  thresholdValue: number;
  thresholdDirection: ThresholdDirection;
  enabled?: boolean;
}

/** Mirror of event-side CohortTarget (dimension optional = "auto"). */
export interface TemplateCohortTarget {
  dimension?: string;
  label: string;
  targetPercent: number;
  /**
   * "AT_MOST" = a cap (stored on the template since ship 2026-10-06, audit r1
   * F3). Read back, a floor is absent; on save every row says it — see
   * `wireTargetDirection`.
   */
  direction?: "AT_MOST";
}

/** GoalAlertConfig minus the event-bound recipient fields
 *  (adminRecipientIds/recipientMode/digestAnchor — server strips them). */
export interface TemplateGoalAlertConfig {
  notifyAdmins?: boolean;
  customEmails?: string[];
  enabled?: boolean;
  weeklyDigest?: boolean;
  digestInterval?: DigestInterval;
  digestWeekday?: Weekday;
}

export interface TemplateGoalConfig {
  goalText?: string;
  kpis?: TemplateGoalKpi[];
  cohortTargets?: TemplateCohortTarget[];
  alertConfig?: TemplateGoalAlertConfig;
}

/** "guestImport" section — a ConditionGroup over COMPANY CONTACT pool fields.
 *  On apply, matching contacts are imported as guests (best-effort, capped at
 *  5000 server-side). Conditions must be non-empty — an empty tree would
 *  match the entire pool, so the backend rejects it. */
export interface TemplateGuestImportConfig {
  conditions: ConditionGroup;
}

// "tableViews" section — event-scoped table views + filters (one wrapper,
// the BE section registry is strictly one-section→one-field). No ids/userIds:
// apply mints fresh ones and assigns the applying user.
export interface TemplateTableView {
  /** Event-scoped TableViewType wire name (GUEST, EMAIL_SENT, CHECK_IN, ACTION_LOG). */
  tableViewType: string;
  name: string;
  description?: string;
  fields: string[];
  /** Verbatim link into the general section's guestDetailLayouts entry ids —
   *  resolves when both sections apply; dangles silently otherwise. */
  guestDetailLayoutId?: string;
  detailLayoutId?: string;
}

export interface TemplateTableFilter {
  tableFilterType: string;
  name: string;
  description?: string;
  filter?: Filter[];
  /** Nested ConditionGroup tree — the only representation for OR-rooted filters. */
  conditions?: ConditionGroup;
}

export interface TemplateTableConfig {
  views?: TemplateTableView[];
  filters?: TemplateTableFilter[];
}

// ── Section payload types ──

export interface TemplateDashboardCard {
  included: boolean;
  sourceEventId?: string;
  sourceEventName?: string;
  name: string;
  width: number;
  height: number;
  x: number;
  y: number;
  theme?: string;
  tableType?: string;
  fields?: string[];
  preSelector?: string;
  filter?: Filter[];
  dashboardName?: string;
}

export interface TemplateDeviceConfig {
  included: boolean;
  sourceEventId?: string;
  sourceEventName?: string;
  name: string;
  canCheckIn: boolean;
  canCheckOut: boolean;
  canViewGuestList: boolean;
  canViewGuestDetails: boolean;
  actions: TemplateDeviceAction[];
}

export interface TemplateDeviceAction {
  type: string;
  offeringIndex?: number;
  treatmentIndex?: number;
  roomIndex?: number;
  fieldKey?: string;
  fieldValue?: string;
}

export interface TemplateEmailConfig {
  included: boolean;
  sourceEventId?: string;
  sourceEventName?: string;
  templateName: string;
  replyToMail?: string;
  senderMail?: string;
  automaticSend?: string;
  languages: string[];
  primaryLanguage: string;
  mailSubject: Record<string, string>;
  html: Record<string, string>;
  texts: Record<string, string>;
  craftJs?: string;
  contentAreaWidth?: number;
  globalFont?: string;
  linkColor?: string;
  attachments?: string[];
  portalLinkEnabled?: boolean;
  portalLinkAttachmentIds?: string[];
  portalLinkExpiryDays?: number;
}

export interface TemplateWorkflow {
  included: boolean;
  sourceEventId?: string;
  sourceEventName?: string;
  name: string;
  enabled: boolean;
  triggers: string[];
  conditions?: ConditionGroup;
  actions: TemplateAction[];
}

export type TemplateAction =
  | { actionType: "SEND_EMAIL"; emailTemplateIndex: number; order: number; markAsContacted?: boolean }
  | { actionType: "UPDATE_GUEST"; fieldKey: string; value: string; order: number }
  | { actionType: "HTTP_REQUEST"; url: string; method: string; headers?: Record<string, string>; bodyTemplate?: string; order: number };

export interface TemplateOfferingConfig {
  included: boolean;
  sourceEventId?: string;
  sourceEventName?: string;
  name: string;
  description?: string;
  bookingMode: string;
  overbookingPct: number;
  minThreshold?: number;
  lanes: TemplateLane[];
  treatments: TemplateTreatment[];
}

export interface TemplateTreatment {
  name: string;
  description?: string;
  durationMinutes: number;
  capacity: number;
  minThreshold?: number;
  imageUrl?: string;
  sortOrder: number;
}

export interface TemplateLane {
  name: string;
  bufferMinutes: number;
  color?: string;
  tags: string[];
  treatmentRefs: number[];
  sortOrder: number;
}

export interface TemplateAttachmentConfig {
  included: boolean;
  sourceEventId?: string;
  sourceEventName?: string;
  name: string;
  assignmentType: string;
  sourceType: string;
  accessType: string;
  targetType: string;
  filters?: Filter[];
  sortOrder: number;
}

// ── Filter re-export for backward compatibility ──
export type { Filter };
